import { create } from "zustand";

// Game state machine modes
export const MODES = {
  MENU: "MENU",
  ON_TRACK: "ON_TRACK",
  AIRBORNE: "AIRBORNE",
  IN_WATER: "IN_WATER",
  ON_GROUND: "ON_GROUND",
  CLIMBING: "CLIMBING",
  RECAP: "RECAP",
  FINISHED: "FINISHED",
};

// Physics constants per world (single flavour now)
export const WORLDS = {
  EARTH: { name: "🌍 Earth", g: 10.0, rhoAir: 1.2, rhoWater: 1000 },
};

// Track presets — two distinct ride experiences
export const TRACKS = {
  TRAINING_VALLEY: {
    name:         "Green Valley",
    subtitle:     "Smooth steel rails · Pine forests · Lakes",
    mu:           0.025,
    airMult:      1.0,   // air density multiplier vs Earth standard
    color:        "#22c55e",
    description:  "Moderate friction. Classic roller-coaster feel through forests and canyon. Good for first rides.",
    emoji:        "🌲",
  },
  DESERT_CANYON: {
    name:         "Desert Canyon",
    subtitle:     "Sand-dusted rails · Red cliffs · Scorching air",
    mu:           0.045,
    airMult:      1.4,   // hot dry air is less dense in reality but dusty tracks add drag
    color:        "#f97316",
    description:  "Higher rail friction and 40 % more aerodynamic drag — descents cap at lower speeds, climbs feel heavier.",
    emoji:        "🏜",
  },
};

// Persistent unlocks (survive page reloads on the website)
const persisted = (key) => {
  try { return localStorage.getItem(key) === "1"; } catch { return false; }
};
const persist = (key) => {
  try { localStorage.setItem(key, "1"); } catch {}
};

export const useGame = create((set, get) => ({
  // ─── App-level screen routing: game | exam | lab | map ───
  screen: "game",
  examUnlocked: persisted("pg_exam_unlocked"),
  challengeUnlocked: true, // always unlocked
  labParams: null, // { r, mu, vWrong, vCorrect, question, whyText } | null

  // ─── Progress ───
  progress: {
    p1Lap:  persisted("pg_p1_lap"),
    p1Exam: persisted("pg_p1_exam"),
  },
  openMap: () => set({ screen: "map" }),
  closeMap: () => set({ screen: "game" }),
  markProgress: (key) => {
    persist("pg_" + key.toLowerCase());
    set((s) => ({ progress: { ...s.progress, [key]: true } }));
  },

  openExam: () => set({ screen: "exam" }),
  closeExam: () => set({ screen: "game" }),
  openLab: (labParams) => set({ screen: "lab", labParams }),
  closeLab: () => set((s) => ({ screen: s.labParams?.fromExam ? "exam" : "game", labParams: null })),
  unlockExam: () => { persist("pg_exam_unlocked"); set({ examUnlocked: true }); },
  unlockChallenge: () => { persist("pg_challenge_unlocked"); set({ challengeUnlocked: true }); },

  // ─── Exam state lives HERE so visiting the Lab never resets the test ───
  examIds: [],        // ids of the randomly drawn questions for this attempt
  examAnswers: {},    // qId → option index
  examSubmitted: false,
  setExamIds: (ids) => set({ examIds: ids }),
  setExamAnswer: (qId, oi) =>
    set((s) => ({ examAnswers: { ...s.examAnswers, [qId]: oi } })),
  submitExamState: () => set({ examSubmitted: true }),
  resetExam: () => set({ examIds: [], examAnswers: {}, examSubmitted: false }),

  // ─── CHALLENGE MODE ───
  challengeGoal: null,     // null | "energy" | "time" | "risk" | "wear"
  challengeMu: 0.3,        // friction slider (challenge only)
  challengeMetrics: { energy: 0, wear: 0, riskTime: 0, crashes: 0 },
  sparksActive: false,
  showChallengeSelect: false,
  setChallengeMu: (mu) => set({ challengeMu: mu }),
  openChallengeSelect: () => set({ showChallengeSelect: true }),
  closeChallengeSelect: () => set({ showChallengeSelect: false }),
  startChallenge: (goal) =>
    set({
      challengeGoal: goal,
      showChallengeSelect: false,
      challengeMetrics: { energy: 0, wear: 0, riskTime: 0, crashes: 0 },
    }),
  exitChallenge: () => set({ challengeGoal: null }),

  // Setup
  mode: MODES.MENU,
  world: "EARTH",
  track: "TRAINING_VALLEY",
  playerName: "PhysicsPro",

  // Cart properties
  mass: 200, // kg
  cartArea: 2.0, // m² (frontal area)
  Cd: 0.8, // drag coefficient cart
  parachuteOpen: false,
  parachuteArea: 0.0, // m² — controlled by Physics Console slider
  parachuteCdA: 1.4, // drag coefficient parachute

  // PHYSICS CONSOLE state
  // targetSpeed is removed in favor of direct Arrow key controls
  parachuteCd: 1.4, // AIRBORNE parachute Cd slider
  displacedVolume: 0.18, // IN_WATER volume of displacement (m³)
  waterDensity: 1000, // IN_WATER ρ slider (950–1100 kg/m³)
  walkSpeed: 2.5, // ON_GROUND target walking/running speed (m/s)
  groundFriction: 0.6, // ON_GROUND ground friction μ (display)
  upcomingRadius: 1e9, // forward lookahead radius (m)
  upcomingSlope: 0, // forward lookahead slope (rad)

  // Live physics state
  s: 0, // arc-length progress along track [0..1]
  v: 0, // speed along track (m/s)
  a: 0, // current acceleration (m/s²)
  N: 0, // normal force (N)
  pos: [0, 5, 0],
  vel: [0, 0, 0], // velocity vector for airborne / water modes
  height: 0,
  fallStart: 0,

  // Game progression
  coins: 50,
  points: 0,
  stationsCrossed: 0,
  totalStations: 4,
  level: 1,
  lap: 1,
  laps: 1,

  // New Training Valley mechanics
  currentZone: "ZONE_1",
  stopwatchActive: false,
  stopwatchTime: 0,
  globalTimer: 0,
  analytics: {
    maxSpeed: 0,
    kinksNavigated: 0,
    sharpestRadius: Infinity,
    maxSpeedExceedance: 0,
  },

  // Recap (educational feedback)
  recap: null, // { reason, question, correctAnswer, parameter, unit }

  // Decision pause state
  paused: false,
  decision: null,
  decisionAttempts: 0,
  greenSinceMs: 0,

  // Consequence preview (wrong-answer slow-motion replay)
  consequencePreview: false,
  slowMotion: false,
  consequenceCountdown: 0,    // 3→2→1→0 countdown before slow-mo starts
  savedDecision: null,        // decision to restore on retry
  savedSpeed: 0,              // the wrong speed the student entered
  savedCorrectAnswer: 0,      // correct answer (shown in consequence modal)
  savedPos: [0, 5, 0],        // cart position at decision trigger
  savedSeg: "A",              // segment at decision trigger
  savedS: 0,                  // s value at decision trigger
  savedV: 5,                  // speed at decision trigger
  retryPrefill: null,         // wrong speed to prefill in DecisionPause on retry

  // Climb animation state
  climbTarget: null,          // THREE.Vector3 to lerp toward
  climbSegment: "A",
  climbS: 0,

  // Equation feed - what equations are active right now
  activeEquations: [],

  // ─── Misconception detection (Predict→Observe→Explain) ───
  // tag → number of times a distractor with this tag was chosen
  misconceptionLog: {},
  // Per-concept prediction stats: kind → { firstTryCorrect, total }
  poeStats: {},

  // ─── Drive-by-Physics hazard system ───
  hazardVmax: {},        // hazardId → v_max the student computed (unlocks HUD target)
  hazardResults: [],     // [{ id, label, maxV, vmax, outcome, points }]
  hazardHUD: null,       // live HUD info for nearest hazard
  lastHazardResult: null,// toast shown after crossing/crashing a hazard
  crashes: 0,            // derailments this run
  // Respawn channel: GameLoop syncs its internal refs from these, then clears
  respawnS: null,
  respawnV: 4,
  forceSpeed: null,      // one-shot speed override (wrong-answer consequence test)

  // Nearest station for ground-mode climb prompt
  nearestStation: null,

  // Actions
  setMode: (mode) => set({ mode }),
  setWorld: (world) => set({ world }),
  setTrack: (track) => set({ track }),
  setPlayerName: (playerName) => set({ playerName }),
  reset: () =>
    set({
      mode: MODES.MENU,
      s: 0,
      v: 0,
      a: 0,
      pos: [0, 0, 0],
      N: 0,
      coins: 50,
      points: 0,
      stationsCrossed: 0,
      parachuteOpen: false,
      parachuteArea: 5.0,
      recap: null,
    }),
  startGame: () =>
    set({
      mode: MODES.ON_TRACK,
      s: 0,
      v: 5,
      a: 0,
      N: 0,
      coins: 50,
      points: 0,
      stationsCrossed: 0,
      parachuteOpen: false,
      parachuteArea: 0,
      targetSpeed: 8,
      displacedVolume: 0.18,
      waterDensity: 1000,
      walkSpeed: 2.5,
      recap: null,
      paused: false,
      decision: null,
      greenSinceMs: 0,
      misconceptionLog: {},
      poeStats: {},
      hazardVmax: {},
      hazardResults: [],
      hazardHUD: null,
      lastHazardResult: null,
      crashes: 0,
      respawnS: null,
      respawnV: 4,
      forceSpeed: null,
      challengeMetrics: { energy: 0, wear: 0, riskTime: 0, crashes: 0 },
      consequencePreview: false,
      slowMotion: false,
      savedDecision: null,
      globalTimer: 0,
      stopwatchTime: 0,
      analytics: {
        maxSpeed: 0,
        kinksNavigated: 0,
        sharpestRadius: Infinity,
        maxSpeedExceedance: 0,
      },
    }),

  // Slider setters
  setTargetSpeed: (v) => set({ targetSpeed: v }),
  setParachuteArea: (a) => set({ parachuteArea: a, parachuteOpen: a > 0.1 }),
  setParachuteCd: (cd) => set({ parachuteCd: cd, parachuteCdA: cd }),
  setDisplacedVolume: (v) => set({ displacedVolume: v }),
  setWaterDensity: (d) => set({ waterDensity: d }),
  setWalkSpeed: (v) => set({ walkSpeed: v }),

  // Update live state from physics loop
  updatePhysics: (patch) => set(patch),

  // Award/deduct coins/points
  awardPoints: (pts) => set((s) => ({ points: s.points + pts })),
  awardCoins: (c) => set((s) => ({ coins: s.coins + c })),
  deductCoins: (c) => set((s) => ({ coins: Math.max(0, s.coins - c) })),

  // Parachute
  toggleParachute: () =>
    set((s) => ({
      parachuteOpen: !s.parachuteOpen,
      // 30 coin tax for opening parachute (educational consequence)
      coins: !s.parachuteOpen ? Math.max(0, s.coins - 30) : s.coins,
    })),
  adjustParachuteArea: (delta) =>
    set((s) => ({
      parachuteArea: Math.max(1, Math.min(25, s.parachuteArea + delta)),
    })),

  // Recap fill-in-the-blank
  triggerRecap: (recap) => set({ mode: MODES.RECAP, recap }),
  resolveRecap: (correct) => {
    if (correct) {
      // Refund and resume
      set((s) => ({ mode: MODES.ON_TRACK, recap: null, v: 5 }));
    } else {
      set((s) => ({
        mode: MODES.ON_TRACK,
        recap: null,
        v: 5,
        coins: Math.max(0, s.coins - 20),
        s: 0,
      }));
    }
  },

  setEquations: (eqs) => set({ activeEquations: eqs }),

  // ─── Misconception logging actions ───
  logMisconception: (tag) =>
    set((s) => ({
      misconceptionLog: {
        ...s.misconceptionLog,
        [tag]: (s.misconceptionLog[tag] || 0) + 1,
      },
    })),
  logPrediction: (kind, firstTryCorrect) =>
    set((s) => {
      const prev = s.poeStats[kind] || { firstTryCorrect: 0, total: 0 };
      return {
        poeStats: {
          ...s.poeStats,
          [kind]: {
            firstTryCorrect: prev.firstTryCorrect + (firstTryCorrect ? 1 : 0),
            total: prev.total + 1,
          },
        },
      };
    }),
  // Tags chosen 2+ times = confirmed misconceptions
  getConfirmedMisconceptions: () => {
    const log = get().misconceptionLog;
    return Object.keys(log).filter((tag) => log[tag] >= 2);
  },
}));
